import {
  ReactElement,
  Ref,
  useEffect,
  useImperativeHandle,
  useRef,
} from 'react';
import { useTranslation } from 'react-i18next';

import {
  Directions,
  InputModes,
  InputTypes,
  PaymentTypes,
} from '@cashier/cashier-model';
import {
  AmountConverter,
  eventBus,
  EventFactory,
  useUIConfiguration,
} from '@cashier/cashier-shared';
import { cn, maskInput } from '@cashier/cashier-ui';

import defaultStyles from './defaultStyles.module.scss';
import inlineStyles from './inlineStyles.module.scss';

interface InputApi {
  ref?: Ref<InputApi>;
  getValue: () => string;
  setValue: (value: string) => void;
}

interface InputProps {
  ref?: Ref<InputApi>;
  name: string;
  label: string;
  type: InputTypes;
  value: string;
  isError?: boolean;
  isDisabled?: boolean;
  min: number;
  max: number;
  onChange: (value: string, isFocused: boolean) => void;
  paymentType: PaymentTypes;
  direction: Directions;
  currency: string | null;
  version: 'v2' | 'v3';
}

export const Input = ({
  ref,
  name,
  label,
  type,
  value,
  min,
  max,
  isError = false,
  isDisabled = false,
  onChange,
  paymentType,
  direction,
  currency,
  version,
}: InputProps): ReactElement => {
  const inputRef = useRef<HTMLInputElement>(null);

  const { t } = useTranslation();

  const inputLabel = useUIConfiguration('input', 'label');
  const inputVariant = useUIConfiguration('amountInput', 'variant');
  const inputBorder = useUIConfiguration('amountInput', 'border');

  const isDefaultLabel = inputLabel === 'default';
  const isStandardInput = inputVariant === 'standard' && version === 'v2';
  const isOutlinedInput = inputVariant === 'outlined' || version === 'v3';

  const containerStyles = isDefaultLabel
    ? cn({
        [defaultStyles.topContainer]: true,
        [defaultStyles.inputErrorBorder]: isError,
      })
    : cn({
        [inlineStyles.inlineContainer]: true,
        [inlineStyles.inputErrorBorder]: isError,
      });

  const inputStyles = isStandardInput
    ? cn({
        [defaultStyles.input]: true,
        [defaultStyles.inputError]: isError,
        [defaultStyles.leftPadding]: !currency,
        [defaultStyles.borderless]: inputBorder === 'none',
      })
    : cn({
        [inlineStyles.input]: true,
        [inlineStyles.inputError]: isError,
        [inlineStyles.active]: Boolean(inputRef.current?.value),
      });

  const currencySymbolStyles = isStandardInput
    ? cn({
        [defaultStyles.currency]: true,
        [defaultStyles.currencyError]: isError,
        [defaultStyles.rtl]: direction === Directions.Rtl,
      })
    : cn({
        [inlineStyles.currency]: true,
        [inlineStyles.currencyError]: isError,
        [inlineStyles.rtl]: direction === Directions.Rtl,
      });

  const minAmount = AmountConverter.toDelimitedNumber(
    AmountConverter.exponentialNotationToNumber(String(min)),
  );

  const maxAmount = AmountConverter.toDelimitedNumber(
    AmountConverter.exponentialNotationToNumber(String(max)),
  );

  useEffect(() => {
    if (!inputRef?.current) return;

    if (value && value !== inputRef.current.value) {
      inputRef.current.value = value;
    }
  }, [value]);

  useImperativeHandle(ref, () => ({
    getValue: () => inputRef.current?.value ?? '',
    setValue: (value) => {
      if (inputRef.current) inputRef.current.value = value;
    },
  }));

  const onChangeHandler = (): void => {
    if (!inputRef?.current) return;

    maskInput(inputRef, name, type);

    onChange(
      inputRef.current.value,
      document.activeElement === inputRef.current,
    );
  };

  const onFocusHandler = (): void => {
    if (!inputRef?.current) return;

    onChange(inputRef.current.value, true);
    eventBus.emit(EventFactory.onFieldFocused(name));
  };

  const onBlurHandler = (): void => {
    if (!inputRef?.current) return;

    onChange(inputRef.current.value, false);
  };

  return (
    <div className={defaultStyles.container}>
      <div className={defaultStyles.labelContainer}>
        <label
          className={cn([
            defaultStyles.label,
            paymentType === PaymentTypes.Deposit
              ? defaultStyles.depositLimitItem
              : defaultStyles.withdrawalLimitItem,
          ])}
          htmlFor={name}
          data-test="amount-input-label"
        >
          {label}
        </label>

        {isOutlinedInput && (
          <div className={defaultStyles.outlinedLimitContainer}>
            <div
              className={defaultStyles.inlineLimits}
              data-test="limits-placeholder"
            >
              {minAmount && `${t('min.label')} ${currency} ${minAmount}`} -{' '}
              {maxAmount && `${t('max.label')} ${currency} ${maxAmount}`}
            </div>
          </div>
        )}
      </div>
      <div className={containerStyles} data-test="amount-input-container">
        {currency && (
          <div
            className={currencySymbolStyles}
            data-test="amount-input-currency"
          >
            {currency}
          </div>
        )}
        <input
          data-test="amount-input"
          id={name}
          type={type || InputTypes.Text}
          name={name}
          ref={inputRef}
          onChange={onChangeHandler}
          min={minAmount}
          max={maxAmount}
          autoComplete="off"
          disabled={isDisabled}
          onFocus={onFocusHandler}
          onBlur={onBlurHandler}
          inputMode={
            type === InputTypes.Integer
              ? InputModes.Numeric
              : InputModes.Decimal
          }
          className={cn({
            [inputStyles]: true,
          })}
        />
      </div>

      {isStandardInput && (
        <div className={defaultStyles.standardLimitContainer}>
          <div
            className={cn([
              defaultStyles.limitItem,
              paymentType === PaymentTypes.Deposit
                ? defaultStyles.depositLimitItem
                : defaultStyles.withdrawalLimitItem,
            ])}
            data-test="min-limit"
          >
            {minAmount && `${t('min.label')} ${minAmount} ${currency || ''}`}
          </div>
          <div
            className={cn([
              defaultStyles.limitItem,
              paymentType === PaymentTypes.Deposit
                ? defaultStyles.depositLimitItem
                : defaultStyles.withdrawalLimitItem,
            ])}
            data-test="max-limit"
          >
            {maxAmount && `${t('max.label')} ${maxAmount} ${currency || ''}`}
          </div>
        </div>
      )}
    </div>
  );
};
